import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  ButtonBase,
  Chip,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import { PageContainer, PageHeader } from "../components/PageHeader";
import SignageChannelPreviewDialog from "../components/SignageChannelPreviewDialog";
import { tokens } from "../theme";
import { foyerApi, type SignageScopeRef } from "../http/foyer";
import type { SignagePlaylist } from "@shared/types/signage";
import { channelPosterUrl } from "@shared/signage";
import { columnsThatFit, useElementWidth } from "../utils/stripScale";
import { PermissionGate } from "../auth/access";

/**
 * Channels - the curated catalogue an operator picks background content from.
 *
 * A "channel" is a GLOBAL-layer signage playlist. There is no separate channel
 * entity: the curated-vs-own distinction is the media layer, so what this page
 * browses is `foyerApi.listSignagePlaylists(...)` filtered to `scope: "global"`.
 */

export interface ChannelCard {
  id: string;
  name: string;
  category: string;
  /** Poster image URL. */
  posterUrl: string | null;
}

export interface SignageChannelsPageProps {
  channels: ChannelCard[];
  onPreview: (id: string) => void;
}

/**
 * The card grid's own numbers, shared with the CSS below so the two cannot
 * drift. `CARD_MIN` is the `minmax()` floor; `GRID_GAP` is MUI `gap={2}`.
 */
const CARD_MIN = 170;
const GRID_GAP = 16;

/**
 * How many cards fit on one row, before it has been measured.
 *
 * Only ever visible for the frame between mount and the first ResizeObserver
 * callback, and deliberately small: guessing high would render a row that
 * immediately shrinks, and a row growing once is the calmer of the two.
 */
const COLUMNS_UNMEASURED = 6;

function ChannelTile({
  channel,
  onPreview,
}: {
  channel: ChannelCard;
  onPreview: () => void;
}) {
  const { t } = useTranslation("signage");
  return (
    /*
     * The WHOLE card is the control, and the only one.
     *
     * It used to carry three icon buttons - preview, edit, delete - which cost
     * the title most of a 170px footer: every name rendered as "Mäss…",
     * "Drone…", "Thrill …". Two of the three did not belong here anyway.
     * Editing and deleting a channel is Distribution's job (Sigge,
     * 2026-09-01); this page is a catalogue you browse, and a catalogue that
     * offers to delete its contents invites exactly one kind of accident.
     *
     * `ButtonBase` rather than an `onClick` on a `Box`, so the card is a real
     * button: reachable by Tab, activated by Enter and Space, and announced as
     * one. A clickable div is none of those things.
     */
    <PermissionGate
      resource="signage"
      level="view"
      place="global"
      deniedProps={{ disabled: true }}
    >
      <ButtonBase
        onClick={onPreview}
        aria-label={t("signageChannelsPage.previewNamed", {
          name: channel.name,
        })}
        sx={{
          display: "block",
          width: "100%",
          textAlign: "left",
          border: `1px solid ${tokens.divider}`,
          borderRadius: "12px",
          overflow: "hidden",
          bgcolor: tokens.surface,
          transition: "border-color 120ms, box-shadow 120ms",
          "&:hover": {
            borderColor: tokens.brand,
            boxShadow: tokens.shadowCard,
          },
          "&.Mui-focusVisible": {
            borderColor: tokens.brand,
            outline: `2px solid ${tokens.brand}`,
            outlineOffset: 2,
          },
          /*
           * The hover affordance, driven from the CARD rather than from the veil
           * itself - the pointer is over the card, and a veil that only lit when
           * the pointer reached its own box would flicker across the gap.
           *
           * Focus is listed with hover deliberately. A keyboard user gets the
           * focus ring either way, but the ring says "this is selected" and the
           * veil says "this will play something"; showing one without the other
           * makes the card mean two different things depending on how you
           * arrived at it.
           */
          "&:hover [data-preview-veil], &.Mui-focusVisible [data-preview-veil]":
            {
              opacity: 1,
            },
        }}
      >
        <Box
          sx={{
            position: "relative",
            aspectRatio: "3 / 4",
            bgcolor: tokens.rowAlt,
            backgroundImage: channel.posterUrl
              ? `url(${channel.posterUrl})`
              : undefined,
            backgroundSize: "cover",
            backgroundPosition: "center",
            display: "grid",
            placeItems: "center",
          }}
        >
          {/* "No POSTER", not "no preview": the card is a preview button now, and
            a tile reading "No preview" under a control offering one says the
            opposite of what it means. This is about missing artwork - 5 of the
            54 imported channels have none upstream. */}
          {!channel.posterUrl && (
            <Typography variant="meta" sx={{ color: tokens.inkFaint }}>
              {t("signageChannelsPage.noPoster")}
            </Typography>
          )}
          <Box
            data-preview-veil
            aria-hidden
            sx={{
              position: "absolute",
              inset: 0,
              display: "grid",
              placeItems: "center",
              // Dark enough to read a white glyph against a bright poster, light
              // enough that you can still see which channel you are hovering.
              bgcolor: "rgba(12, 16, 24, 0.42)",
              opacity: 0,
              transition: "opacity 140ms ease",
              "@media (prefers-reduced-motion: reduce)": {
                transition: "none",
              },
            }}
          >
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                bgcolor: "rgba(255, 255, 255, 0.92)",
                color: tokens.ink,
              }}
            >
              <PlayArrowRoundedIcon />
            </Box>
          </Box>
        </Box>
        {/* `minWidth: 0` on a full-width block: without it the ellipsis never
          engages and a long name pushes the card wider than its grid track. */}
        <Box sx={{ px: 1.5, py: 1.25, minWidth: 0 }}>
          <Typography
            variant="body2"
            noWrap
            sx={{ fontWeight: "fontWeightSemibold", color: tokens.ink }}
          >
            {channel.name}
          </Typography>
          <Typography variant="meta" noWrap sx={{ color: tokens.inkMuted }}>
            {channel.category}
          </Typography>
        </Box>
      </ButtonBase>
    </PermissionGate>
  );
}

/**
 * One category and its cards.
 *
 * A component rather than a block inside `.map` because it MEASURES itself:
 * how many cards a collapsed row shows is a function of how many columns fit,
 * and a hook cannot be called from inside a loop body.
 */
function CategoryRow({
  category,
  list,
  open,
  onToggle,
  onPreview,
}: {
  category: string;
  list: ChannelCard[];
  open: boolean;
  onToggle: () => void;
  onPreview: (id: string) => void;
}) {
  const { t } = useTranslation(["signage", "glossary"]);
  const gridRef = useRef<HTMLDivElement>(null);
  const perRow = columnsThatFit(
    useElementWidth(gridRef),
    CARD_MIN,
    GRID_GAP,
    COLUMNS_UNMEASURED
  );
  // Exactly one row when collapsed, whatever the window is - so the cards fill
  // the width the header already runs to.
  const shown = open ? list : list.slice(0, perRow);

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1.5 }}
      >
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography
            sx={{
              typography: "title",
              fontWeight: "fontWeightBold",
              color: tokens.ink,
            }}
          >
            {category}
          </Typography>
          <Chip
            label={t("glossary:channelCount", { count: list.length })}
            size="small"
            sx={{
              height: 22,
              typography: "micro",
              bgcolor: tokens.brandTintSoft,
              color: tokens.brand,
            }}
          />
        </Stack>
        {/* Offered only when something is actually hidden. Widen the window
            until the whole category fits on one row and the control retires
            itself, rather than promising to reveal nothing. */}
        {list.length > perRow && (
          <Button
            size="small"
            endIcon={
              <ExpandMoreIcon
                sx={{
                  transform: open ? "rotate(180deg)" : "none",
                  transition: "transform 120ms",
                }}
              />
            }
            onClick={onToggle}
          >
            {open
              ? t("signageChannelsPage.showFewer")
              : t("signageChannelsPage.showAll", { count: list.length })}
          </Button>
        )}
      </Stack>

      <Box
        ref={gridRef}
        sx={{
          display: "grid",
          gap: `${GRID_GAP}px`,
          gridTemplateColumns: `repeat(auto-fill, minmax(${CARD_MIN}px, 1fr))`,
        }}
      >
        {shown.map((c) => (
          <ChannelTile
            key={c.id}
            channel={c}
            onPreview={() => onPreview(c.id)}
          />
        ))}
      </Box>
    </Box>
  );
}

export function SignageChannelsPage({
  channels,
  onPreview,
}: SignageChannelsPageProps) {
  const { t } = useTranslation("signage");
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("all");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const categories = useMemo(
    () => [...new Set(channels.map((c) => c.category))].sort(),
    [channels]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return channels.filter(
      (c) =>
        (category === "all" || c.category === category) &&
        (!q || c.name.toLowerCase().includes(q))
    );
  }, [channels, query, category]);

  const grouped = useMemo(() => {
    const map = new Map<string, ChannelCard[]>();
    for (const c of filtered) {
      const list = map.get(c.category) ?? [];
      list.push(c);
      map.set(c.category, list);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  return (
    <PageContainer>
      <PageHeader
        eyebrow={t("signageChannelsPage.eyebrow")}
        title={t("signageChannelsPage.title")}
        subtitle={t("signageChannelsPage.subtitle")}
      />

      <Stack
        direction={{ xs: "column", sm: "row" }}
        gap={1.5}
        sx={{ mb: 3 }}
        justifyContent="space-between"
      >
        <TextField
          size="small"
          placeholder={t("signageChannelsPage.searchPlaceholder")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{ width: { xs: "100%", sm: 320 } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select
          size="small"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          sx={{ width: { xs: "100%", sm: 220 } }}
        >
          <MenuItem value="all">
            {t("signageChannelsPage.allCategories")}
          </MenuItem>
          {categories.map((c) => (
            <MenuItem key={c} value={c}>
              {c}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      {grouped.length === 0 ? (
        <Box
          sx={{
            border: `1px solid ${tokens.divider}`,
            borderRadius: "12px",
            p: 5,
            textAlign: "center",
            bgcolor: tokens.surface,
          }}
        >
          <Typography
            sx={{ fontWeight: "fontWeightSemibold", color: tokens.ink }}
          >
            {channels.length === 0
              ? t("signageChannelsPage.noChannels")
              : t("signageChannelsPage.noMatches")}
          </Typography>
          {channels.length === 0 && (
            <Typography
              variant="bodySm"
              sx={{ color: tokens.inkMuted, mt: 0.5 }}
            >
              {t("signageChannelsPage.noChannelsHint")}
            </Typography>
          )}
        </Box>
      ) : (
        <Stack gap={4}>
          {grouped.map(([cat, list]) => (
            <CategoryRow
              key={cat}
              category={cat}
              list={list}
              open={expanded.has(cat)}
              onToggle={() =>
                setExpanded((prev) => {
                  const next = new Set(prev);
                  if (next.has(cat)) next.delete(cat);
                  else next.add(cat);
                  return next;
                })
              }
              onPreview={onPreview}
            />
          ))}
        </Stack>
      )}

      <Typography variant="bodySm" sx={{ color: tokens.inkMuted, mt: 3 }}>
        {t("signageChannelsPage.showing", {
          shown: filtered.length,
          total: channels.length,
        })}
      </Typography>
    </PageContainer>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * Channels, wired.
 *
 * A "channel" is a GLOBAL-layer playlist - there is no separate entity - so
 * this reads the same list every other signage page reads and keeps only the
 * curated rows. The scope changes nothing about the READ: the catalogue on
 * offer is the platform's, not the customer's.
 *
 * ## Read-only, and that is a correction (Sigge, 2026-09-05)
 *
 * `BE-40` put a Nenda-admin "New channel" button here, on the reasoning that
 * an admin editing the platform's catalogue from inside one hotel's view was
 * still just editing the platform's catalogue - so every mutation addressed
 * `{kind: "global"}` regardless of the scope in the URL. Correct about the
 * WRITE, wrong about where it should be invited from: this is the surface a
 * CUSTOMER browses to pick what to schedule, and "which channel can I edit"
 * must never be a question of which premise's URL an admin happens to be
 * looking from. Authoring - create, edit, delete - now lives entirely on
 * Distribution's Channel management page (`ChannelsPage.tsx`, Effect filter),
 * which is Nenda-staff-only by construction. This page only ever previews.
 */
export default function SignageChannelsContainer({
  premiseId,
  companyId,
}: {
  /** Exactly one is set: a location, or the whole customer. */
  premiseId?: string;
  companyId?: string;
}) {
  const { t } = useTranslation("signage");
  const scope: SignageScopeRef = premiseId
    ? { kind: "premise", premiseId }
    : { kind: "company", companyId: companyId ?? "" };
  const scopeKey = premiseId ?? companyId ?? "";

  const [playlists, setPlaylists] = useState<SignagePlaylist[]>([]);
  /** The channel whose lightbox is open, or null. */
  const [previewing, setPreviewing] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    foyerApi
      .listSignagePlaylistsIn(scope)
      .then((list) => {
        if (!alive) return;
        setPlaylists(list.filter((p) => p.scope === "global"));
      })
      .catch(() => alive && setPlaylists([]));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope.kind, scopeKey]);

  const channels: ChannelCard[] = useMemo(
    () =>
      playlists.map((p) => ({
        id: p.id,
        name: p.name,
        // Channels with no category still have to appear somewhere.
        category: p.category?.trim() || t("signageChannelsPage.uncategorised"),
        posterUrl: channelPosterUrl(p),
      })),
    [playlists, t]
  );

  return (
    <>
      <SignageChannelsPage
        channels={channels}
        onPreview={(id) => setPreviewing(id)}
      />
      <SignageChannelPreviewDialog
        channelId={previewing}
        channelName={
          playlists.find((p) => p.id === previewing)?.name ??
          t("signageChannelsPage.channel")
        }
        onClose={() => setPreviewing(null)}
      />
    </>
  );
}
